import React, { useState } from 'react';
import Modal from '../../../../components/shareable/modal';

const conveyorTemplate = {
  item_number: '',
  wide: '',
  conveyor_name: '',
  conveyor_style: '',
  // module_id: '',
  powered_or_non_powered: '',
  part_number: '',
  length: '',
  babs_cost: '',
  list: '',
  total: '',
  image_name: '',
  image: null,
};

const CreateConveyorModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState(conveyorTemplate);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) { setErrors(prev => ({ ...prev, [name]: '' })); }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    // Clear error when user starts typing
    if (errors[name]) { setErrors(prev => ({ ...prev, [name]: '' })); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 5 * 1024 * 1024;
      
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        return;
      }
      
      if (file.size > maxSize) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setFormData((prev) => ({ 
        ...prev, 
        image: file,
        image_name: file.name // Automatically set image_name from the file name
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.part_number.trim()) { newErrors.part_number = 'Part number is required'; }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) { return; }
    
    setUploading(true);
    
    try {
      await onSave(formData);
      if (imagePreview) { URL.revokeObjectURL(imagePreview); }
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null, image_name: '' }));
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const numberFields = ['item_number', 'length', 'babs_cost', 'list', 'total'];

  // Fields to exclude from the form (will be handled automatically)
  const excludedFields = ['image', 'image_name', 'temp_quote_qty', 'infeed_ordered', 'outfeed_ordered', 'babs_total'];

  // Custom labels for specific fields
  const getFieldLabel = (field) => {
    const labelMap = {
      'conveyor_name': 'Conveyor Name / Description',
      'babs_cost': 'Our Cost'
    };
    
    return labelMap[field] || field.replace(/_/g, ' ');
  };

  return (
    <Modal isOpen={isOpen} width='4xl' onClose={onClose}>
      <h2 className="text-lg font-semibold mb-4">Add New Conveyor</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-4 max-h-96 overflow-y-auto p-2">
          {/* Image upload field */}
          <div className="col-span-3">
            <label className="block text-sm font-medium mb-1">Image</label>
            <div className="flex items-center">
              {formData.image ? (
                <div className="relative w-full">
                  <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <div className="mb-2 w-16 h-16 flex items-center justify-center overflow-hidden rounded-md bg-gray-100">
                      <img src={imagePreview} alt="Preview" className="max-w-full max-h-full object-contain" />
                    </div>
                    
                    <div className="flex flex-col items-center justify-center w-full">
                      <div className="flex items-center justify-center w-full">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-gray-600 text-center truncate max-w-xs">
                          {formData.image.name}
                        </span>
                      </div>
                      <button type="button" onClick={removeImage} className="mt-2 text-red-500 hover:text-red-700 text-sm flex items-center" >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500">Click to upload image</p>
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              )}
            </div>
          </div>

          {/* Form fields - exclude image and image_name from the form inputs */}
          {Object.keys(conveyorTemplate)
            .filter(field => !excludedFields.includes(field))
            .map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium mb-1 capitalize">
                  {getFieldLabel(field)}
                  {field === 'part_number' && <span className="text-red-500 ml-1">*</span>}
                </label>
                {numberFields.includes(field) ? (
                  <input
                    type="number"
                    name={field}
                    value={formData[field]}
                    onChange={handleNumberChange}
                    className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${getFieldLabel(field)}`}
                    step={field.includes('cost') || field.includes('price') || field.includes('list') ? '0.01' : '1'}
                  />
                ) : (
                  <input
                    type="text"
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded hover:border-red-300 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors ${errors[field] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${getFieldLabel(field)}`}
                  />
                )}
                {errors[field] && (
                  <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
                )}
              </div>
            ))}
        </div>
        
        <div className="flex justify-end space-x-2 mt-4">
          <button type="button" onClick={onClose} className="px-8 py-2 bg-gray-300 rounded hover:bg-gray-400" disabled={uploading} >
            Cancel
          </button>
          <button type="submit" className="px-8 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50" disabled={uploading} >
            {uploading ? 'Uploading...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateConveyorModal;
